/* "Try it" modal for an AI feature (prototype aiModal): source → reading → review extracted fields. */
import { useId, useState } from "react";
import { ApiError } from "../../data/provider";
import { useAiExtract } from "../../data/hooks";
import type { AiExtractResponse, AiFeature } from "../../data/types";
import { useI18n } from "../../i18n";
import { Alert, Button, Icon, Modal, errMsg, useToast } from "../../ui";

/** Human labels for extracted keys. */
const LABELS: Record<string, [string, string]> = {
  nameEn: ["Full name (EN)", "الاسم الكامل (إنجليزي)"], nameAr: ["Full name (AR)", "الاسم الكامل (عربي)"], idNumber: ["ID / Iqama number", "رقم الهوية / الإقامة"],
  nationality: ["Nationality", "الجنسية"], dob: ["Date of birth", "تاريخ الميلاد"], expiry: ["Expiry", "الانتهاء"], amount: ["Amount", "المبلغ"], date: ["Date", "التاريخ"],
  ref: ["Reference", "المرجع"], sender: ["Sender", "المحوّل"], bank: ["Bank", "البنك"], vendor: ["Vendor", "المورد"], vatNo: ["Vendor VAT no.", "الرقم الضريبي للمورد"],
  vat: ["VAT", "الضريبة"], category: ["Category", "الفئة"], type: ["Type", "النوع"], from: ["From", "من"], to: ["To", "إلى"], days: ["Days", "الأيام"], note: ["Reason", "السبب"],
  mapped: ["Columns mapped", "الأعمدة المطابقة"], columns: ["Columns found", "الأعمدة الموجودة"], body: ["Draft", "المسودة"], summary: ["Summary", "الملخص"],
  basic: ["Basic salary", "الراتب الأساسي"], housing: ["Housing", "السكن"], transport: ["Transport", "النقل"], title: ["Job title", "المسمى الوظيفي"], start: ["Start date", "تاريخ البدء"],
};

export function AiTryModal({ f, residency, onClose }: { f: AiFeature; residency: boolean; onClose: () => void }) {
  const { t, L } = useI18n();
  const toast = useToast();
  const extract = useAiExtract();
  const textId = useId();
  const [text, setText] = useState("");
  const [res, setRes] = useState<AiExtractResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [use, setUse] = useState<Record<string, boolean>>({});
  const [vals, setVals] = useState<Record<string, string>>({});

  const run = async (sample: boolean) => {
    setErr(null);
    try {
      const r = await extract.mutateAsync({ feature: f.id, sample: sample ? "default" : undefined, text: sample ? undefined : text.trim() || undefined });
      setRes(r);
      setUse(Object.fromEntries(Object.keys(r.fields).map((k) => [k, true])));
      setVals(Object.fromEntries(Object.entries(r.fields).map(([k, v]) => [k, v == null ? "" : String(v)])));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setErr(/credit/i.test(e.message) ? L("Your company has used all AI credits for this month. The owner can add a +1,000 credits pack in Billing.", "استنفدت منشأتك رصيد الذكاء لهذا الشهر. يمكن للمالك إضافة باقة 1,000 رصيد من الفوترة.") : L("This AI feature is turned off for your company.", "هذه الميزة متوقفة لمنشأتك."));
      else setErr(errMsg(e));
    }
  };
  const conf = res ? Math.round(res.confidence * 100) : 0;
  const title = <><span className="aitag"><Icon name="sparkle" className="s" />AI</span> {L(f.name, f.nameAr)}</>;

  let body, foot;
  if (extract.isPending) {
    body = (
      <div className="stack" style={{ alignItems: "center", padding: "24px 0" }} data-testid="ai-reading">
        <div className="aithink"><span className="aidots"><i /><i /><i /></span>{L("Reading…", "جارٍ القراءة…")}</div>
        <div className="skel" style={{ height: 12, width: "80%" }} /><div className="skel" style={{ height: 12, width: "65%" }} /><div className="skel" style={{ height: 12, width: "72%" }} />
      </div>
    );
  } else if (!res) {
    body = (
      <div className="stack">
        <p className="muted sm" style={{ margin: 0 }}>{L(f.description, f.descriptionAr)}</p>
        {err && <Alert tone="bad" title={err} testId="ai-error" />}
        <div className="field">
          <label className="flabel" htmlFor={textId}>{L("Paste text (email, WhatsApp message or table)", "الصق نصًا (بريد أو رسالة أو جدول)")}</label>
          <textarea id={textId} className="inp" rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="ai-text" />
        </div>
        <Button variant="ghost sm" icon="eye" onClick={() => run(true)} data-testid="ai-sample">{L("Try with a sample", "جرّب بمثال")}</Button>
        <div className="aiinfo"><Icon name="shield" className="s" /><span>{L("Model: ", "النموذج: ")}<b className="num">{f.model}</b> · {f.pii && residency ? L("personal data stays on approved providers only", "البيانات الشخصية تُرسل للمزودين المعتمدين فقط") : f.pii ? L("contains personal data", "يحتوي بيانات شخصية") : L("no personal data", "بدون بيانات شخصية")} · {L("1 credit", "رصيد واحد")} · {L("nothing is saved until you apply", "لا يُحفظ شيء قبل التطبيق")}</span></div>
      </div>
    );
    foot = <><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" icon="sparkle" disabled={!text.trim()} onClick={() => run(false)} data-testid="ai-extract">{L("Extract", "استخراج")}</Button></>;
  } else {
    const keys = Object.keys(res.fields);
    body = (
      <div className="stack" data-testid="ai-result">
        <Alert tone="ok" tint title={L(keys.length + " fields extracted", "تم استخراج " + keys.length + " حقول")} desc={L("Untick anything you don't want. Low-confidence values are highlighted — please check them.", "ألغِ تحديد ما لا تريده. القيم منخفضة الدقة مظللة، يرجى مراجعتها.")} />
        <div className="tw">
          <table className="tbl compact">
            <thead><tr><th style={{ width: 36 }} /><th>{L("Field", "الحقل")}</th><th>{L("Value", "القيمة")}</th><th className="end">{L("Confidence", "الدقة")}</th></tr></thead>
            <tbody>
              {keys.map((k) => {
                const lab = LABELS[k] ? L(...LABELS[k]) : k;
                return (
                  <tr key={k} className={conf < 90 ? "lowconf" : ""}>
                    <td><input type="checkbox" checked={!!use[k]} onChange={(e) => setUse({ ...use, [k]: e.target.checked })} aria-label={L("Use ", "استخدام ") + lab} /></td>
                    <td className="sm">{lab}</td>
                    <td>{k === "body" ? <textarea className="inp" rows={4} value={vals[k]} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} aria-label={lab} /> : <input className="inp" style={{ minHeight: 34 }} value={vals[k]} dir={k === "nameAr" ? "rtl" : undefined} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} aria-label={lab} />}</td>
                    <td className="end"><span className={"chip " + (conf >= 95 ? "ok" : conf >= 90 ? "info" : "warn")}>{conf}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="aiinfo"><Icon name="clock" className="s" /><span>{L(`Saves about ${f.saveMinutes} minutes of typing · `, `يوفر نحو ${f.saveMinutes} دقائق من الإدخال · `)}<span className="num">{res.model}</span> · {L(res.creditsUsed + " credit used", "استُخدم " + res.creditsUsed + " رصيد")}</span></div>
      </div>
    );
    foot = <><Button variant="ghost" onClick={() => { setRes(null); setErr(null); }}>{L("Start over", "البدء من جديد")}</Button><Button variant="pri" icon="check" onClick={() => { toast(L(Object.values(use).filter(Boolean).length + " fields ready — open the matching form to apply them", Object.values(use).filter(Boolean).length + " حقول جاهزة — افتح النموذج المناسب لتطبيقها")); onClose(); }} data-testid="ai-apply">{L("Apply", "تطبيق")}</Button></>;
  }
  return <Modal title={title} className="wide" onClose={onClose} foot={foot} testId="ai-modal">{body}</Modal>;
}
